import { useCallback, useState } from 'react'

import { InjectionTokens } from '@/controller/tokens'

import { UserChart } from '@/domain/models/UserChart'
import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'
import { EnsureUserChartTilesUseCase } from '@/domain/useCases/UserCharts/EnsureUserChartTilesUseCase'

import { userChartTilesUrlTemplate } from '@/data/UserChartRepository/userChartTileStore'

import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { MapImperativeInterface } from '@/presentation/modules/MapScreen/interfaces/MapImperativeInterface'

// Modest inset so the freshly framed chart is not flush against the viewport edges.
const MAP_FIT_PADDING: [number, number, number, number] = [60, 60, 60, 60]

/**
 * Shared "show a user chart on the map" capability, reused by the toolbar
 * (ChartsTool) and by the chart edition screen. It makes sure the chart's tiles
 * are available (charts synced as metadata only have them fetched on demand),
 * then registers the chart as an active raster overlay and frames it. It does
 * not touch the base map or the aeronautical charts — activating a user chart
 * preserves whatever the user already has displayed.
 *
 * Metrics are intentionally left to the callers, since the event name and
 * context differ between the toolbar and the edition screen.
 */
export function useUserChartMapActivation() {
  const [, mapLayerStateMutator] = useGlobalState<MapLayerState, MapLayerStateProps>(InjectionTokens.MapLayerState)

  const [activatingChartId, setActivatingChartId] = useState<string | null>(null)

  const activateUserChart = useCallback(
    async (chart: UserChart): Promise<boolean> => {
      const id = chart.id.toString()
      if (activatingChartId) return false

      // Only activate once tiles are ready so Mapbox does not cache the
      // transparent fallback for uncovered tiles.
      setActivatingChartId(id)
      // Recover old charts whose processed tiles are missing on the server by
      // re-converting the original (if it is still there); the chart carries the
      // file name so the converter picks the right processing path.
      const ensured = await new EnsureUserChartTilesUseCase().execute(id, {
        recoverFromOriginal: true,
        originalFileName: chart.originalFileName
      })
      setActivatingChartId(null)
      if (ensured.isFailure) return false

      // Add the user chart on top of the current map without touching the base
      // map or the aeronautical charts: activating a user chart must preserve
      // whatever the user already has displayed. (The user chart is stacked at
      // `stackIds.userCharts`, above the aeronautical chart layers.)
      mapLayerStateMutator.addActiveUserChart({
        id,
        name: chart.name,
        bounds: chart.boundaries,
        minZoom: chart.minZoom,
        maxZoom: chart.maxZoom,
        tilesUrlTemplate: userChartTilesUrlTemplate(id)
      })

      // Center the map on the chart's extent. This runs only on an explicit
      // activation (list/toolbar toggle, "show on map") — never on the persisted
      // active charts restored at boot, which never go through this hook.
      const bounds = chart.boundaries
      if (bounds?.length === 2 && bounds[0]?.length === 2 && bounds[1]?.length === 2) {
        MapImperativeInterface.flyToBounds([bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]], MAP_FIT_PADDING)
      }

      return true
    },
    [activatingChartId, mapLayerStateMutator]
  )

  return { activateUserChart, activatingChartId }
}
